/*
 * Solitaire — the rules of the game on the table, as a page a person reads
 * once and understands: the goal, a picture of THIS table with its places
 * numbered, every kind of move as a small scene of real cards (the faces the
 * table draws — table.face), this variant's scoring, how to play here, and
 * what the verdict and the deal number mean.
 *
 * Built when the dialog opens (app.js openRules), for the game AND variant on
 * the table. Words come from js.r* in the translations; nothing here is text.
 * Why the rules are told this way — docs/why/solitaire.md, «Правила».
 */
(function (root) {
    'use strict';

    var NS = 'http://www.w3.org/2000/svg';

    function el(tag, cls, text) {
        var n = document.createElement(tag);
        if (cls) n.className = cls;
        if (text != null) n.textContent = text;
        return n;
    }
    function icon(id) {
        var svg = document.createElementNS(NS, 'svg');
        svg.setAttribute('viewBox', '0 0 24 24');
        svg.setAttribute('aria-hidden', 'true');
        var use = document.createElementNS(NS, 'use');
        use.setAttribute('href', '#' + id);
        svg.appendChild(use);
        return svg;
    }

    function build(body, ctx) {
        var S = ctx.S, t = ctx.t, game = ctx.game, v = ctx.v || {};
        // a card by its name: 'QH', 'TS' — rank letters A23456789TJQK, suits SHDC
        function C(name, up) {
            var r = 'A23456789TJQK'.indexOf(name[0]), s = 'SHDC'.indexOf(name[1]);
            return S.mk(0, s * 13 + r, up !== false);
        }
        // Spider's one-suit game has only spades, its two-suit game spades and hearts.
        function sp(name) {
            if (game !== 'spider') return name;
            var s = name[1];
            if (v.suits === 1) s = 'S';
            else if (v.suits === 2) s = (s === 'H' || s === 'D') ? 'H' : 'S';
            return name[0] + s;
        }
        function fill(tpl, vars) {
            return String(tpl).replace(/:(\w+)/g, function (m, k) { return vars && vars[k] != null ? String(vars[k]) : m; });
        }

        // Card size: what the dialog's width allows, never smaller than a card the eye can read.
        var avail = Math.max(260, Math.min(700, (root.innerWidth || 700) - 16) - 28 - 18);
        var cols = game === 'spider' ? 10 : (game === 'freecell' ? 8 : 7);
        var bigCw = Math.max(26, Math.min(58, Math.floor((avail - (cols - 1) * 4) / cols)));
        var narrow = avail < 470;
        var exCw = narrow ? 40 : 46;

        // ── a scene: felt with cards placed in pixels ───────────────────────
        function scene(cw, wCards, hCards) {
            var ch = cw * 1.4, gap = 4;
            var wrap = el('div', 'sol-rs'), inner = el('div', 'sol-rs-in');
            inner.style.setProperty('--cw', cw + 'px');
            inner.style.width = Math.round(wCards * cw + (Math.ceil(wCards) - 1) * gap) + 'px';
            inner.style.height = Math.round(hCards * ch) + 'px';
            wrap.appendChild(inner);
            var z = 0;
            function X(col) { return col * (cw + gap); }
            function place(node, x, y) {
                node.style.setProperty('--tx', x + 'px');
                node.style.setProperty('--ty', y + 'px');
                node.style.setProperty('--z', String(++z));
                inner.appendChild(node);
                return node;
            }
            return {
                el: wrap, cw: cw, ch: ch, X: X,
                card: function (c, col, y) { return place(ctx.face(c), X(col), y); },
                back: function (col, y) { var n = el('div', 'sc dn'); n.setAttribute('aria-hidden', 'true'); return place(n, X(col), y); },
                slot: function (col, y, suit) {
                    var n = el('div', 'sol-slot' + (suit == null ? '' : ' sol-slot-found'));
                    if (suit != null) { var m = icon('sol-s' + suit); m.setAttribute('class', 'sol-slot-mark'); n.appendChild(m); }
                    return place(n, X(col), y);
                },
                // a column: n face-down cards, then the named face-up cards
                column: function (col, y, down, ups) {
                    var yy = y, i;
                    for (i = 0; i < down; i++) { this.back(col, yy); yy += ch * 0.12; }
                    for (i = 0; i < ups.length; i++) { this.card(typeof ups[i] === 'string' ? C(ups[i]) : ups[i], col, yy); yy += ch * 0.3; }
                    return yy - ch * 0.3;
                },
                num: function (n, x, y) {
                    var b = el('span', 'sol-rn', String(n));
                    b.style.left = x + 'px'; b.style.top = y + 'px';
                    b.setAttribute('aria-hidden', 'true');
                    inner.appendChild(b);
                },
                // an arrow of the move, from one point to another, slightly bent
                arrow: function (x1, y1, x2, y2, bend) {
                    var svg = document.createElementNS(NS, 'svg');
                    svg.setAttribute('class', 'sol-ra');
                    svg.setAttribute('width', inner.style.width);
                    svg.setAttribute('height', inner.style.height);
                    svg.setAttribute('aria-hidden', 'true');
                    var mx = (x1 + x2) / 2, my = (y1 + y2) / 2 - (bend == null ? 18 : bend);
                    var path = document.createElementNS(NS, 'path');
                    path.setAttribute('d', 'M' + x1 + ' ' + y1 + ' Q' + mx + ' ' + my + ' ' + x2 + ' ' + y2);
                    svg.appendChild(path);
                    // the head points along the end of the curve
                    var a = Math.atan2(y2 - my, x2 - mx), L = 9, W = 5;
                    var hx1 = x2 - L * Math.cos(a) + W * Math.sin(a), hy1 = y2 - L * Math.sin(a) - W * Math.cos(a);
                    var hx2 = x2 - L * Math.cos(a) - W * Math.sin(a), hy2 = y2 - L * Math.sin(a) + W * Math.cos(a);
                    var head = document.createElementNS(NS, 'path');
                    head.setAttribute('class', 'head');
                    head.setAttribute('d', 'M' + x2 + ' ' + y2 + ' L' + hx1.toFixed(1) + ' ' + hy1.toFixed(1) + ' L' + hx2.toFixed(1) + ' ' + hy2.toFixed(1) + 'Z');
                    svg.appendChild(head);
                    inner.appendChild(svg);
                },
                mark: function (ok) {
                    var m = el('span', 'sol-mk ' + (ok ? 'ok' : 'no'));
                    m.appendChild(icon(ok ? 'sol-i-ok' : 'sol-i-no'));
                    m.setAttribute('role', 'img');
                    m.setAttribute('aria-label', t(ok ? 'r_yes' : 'r_no'));
                    wrap.appendChild(m);
                }
            };
        }
        function figure(sc, caption, wide) {
            var f = el('figure', 'sol-ex' + (wide ? ' wide' : ''));
            f.appendChild(sc.el);
            f.appendChild(el('figcaption', null, caption));
            return f;
        }
        function h(text) { body.appendChild(el('h4', null, text)); }
        function p(text, cls) { if (text) body.appendChild(el('p', cls || null, text)); }
        function grid(list) {
            var g = el('div', 'sol-rx');
            list.forEach(function (f) { if (f) g.appendChild(f); });
            body.appendChild(g);
        }
        function legend(items) {
            var ol = el('ol', 'sol-rl');
            items.forEach(function (it, i) {
                var li = el('li');
                li.appendChild(el('span', 'sol-rn', String(i + 1)));
                var txt = el('span'), dot = it.indexOf('. ');
                if (dot > 0) { txt.appendChild(el('b', null, it.slice(0, dot + 1))); txt.appendChild(document.createTextNode(' ' + it.slice(dot + 2))); }
                else txt.textContent = it;
                li.appendChild(txt);
                ol.appendChild(li);
            });
            body.appendChild(ol);
        }

        // Two cards in a column: the lower one lies on the upper one.
        function onTop(upper, lower, ok) {
            var sc = scene(exCw, 1, 1.32);
            sc.card(C(upper), 0, 0);
            sc.card(C(lower), 0, sc.ch * 0.32);
            sc.mark(ok);
            return sc;
        }

        // ── 1. the goal ─────────────────────────────────────────────────────
        var G = game === 'spider' ? 'rs' : (game === 'freecell' ? 'rf' : 'rk');
        h(t('rh_goal'));
        p(t(G + '_goal'), 'sol-r-lead');

        // ── 2. the table, its places numbered ───────────────────────────────
        h(t('rh_table'));
        var cw = bigCw, sc, ch;
        if (game === 'klondike') {
            sc = scene(cw, 7, 1.18 + 0.12 * 6 + 1.02); ch = sc.ch;
            var ty = ch * 1.18;
            sc.back(0, 0); sc.back(0, 0);
            var drawn = v.draw === 3 ? ['9C', '4H', 'JS'] : ['JS'];
            drawn.forEach(function (n, i) { sc.card(C(n), 1, 0).style.setProperty('--tx', (sc.X(1) + i * cw * 0.28) + 'px'); });
            // the homes stand in the table's order: spades, hearts, diamonds, clubs
            sc.slot(3, 0, 0); sc.card(C('AH'), 4, 0); sc.card(C('2H'), 4, 0); sc.slot(5, 0, 2); sc.slot(6, 0, 3);
            var tops = ['KS', '7D', 'QC', '3S', '9H', '5C', '8D'];
            for (var i = 0; i < 7; i++) sc.column(i, ty, i, i === 3 ? ['3S', '2D'] : [tops[i]]);
            sc.num(1, sc.X(1) - 15, -7); sc.num(2, sc.X(2) - 15, -7); sc.num(3, sc.X(4) - 15, -7); sc.num(4, sc.X(1) - 15, ty - 7);
            body.appendChild(sc.el);
            legend([t(v.draw === 3 ? 'rk_l_stock3' : 'rk_l_stock1'), t(v.draw === 3 ? 'rk_l_waste3' : 'rk_l_waste'), t('rk_l_found'), t('rk_l_tab')]);
        } else if (game === 'spider') {
            sc = scene(cw, 10, 1.2 + 0.12 * 5 + 0.3 + 1.02); ch = sc.ch;
            var sy = ch * 1.2;
            for (var k = 0; k < 5; k++) sc.back(0, 0).style.setProperty('--tx', (k * cw * 0.12) + 'px');
            sc.card(C(sp('KS')), 2, 0);
            for (k = 3; k < 10; k++) sc.slot(k, 0);
            var stops = ['9H', '4S', 'QS', '7H', 'TS', '2H', '6S', 'JH', '3S', '8S'];
            for (k = 0; k < 10; k++) sc.column(k, sy, k < 4 ? 5 : 4, k === 2 ? [sp('QS'), sp('JS')] : [sp(stops[k])]);
            sc.num(1, sc.X(1) - 15, -7); sc.num(2, sc.X(3) - 15, -7); sc.num(3, sc.X(1) - 15, sy - 7);
            body.appendChild(sc.el);
            legend([t('rs_l_stock'), t('rs_l_found'), t('rs_l_tab')]);
            p(t('rs_suits' + (v.suits || 2)));
        } else {
            sc = scene(cw, 8, 1.2 + 0.3 * 3 + 1.02); ch = sc.ch;
            var fy = ch * 1.2;
            sc.card(C('9D'), 0, 0); sc.slot(1, 0); sc.slot(2, 0); sc.slot(3, 0);
            sc.slot(4, 0, 0); sc.slot(5, 0, 1); sc.slot(6, 0, 2); sc.card(C('AC'), 7, 0);
            var fcols = [['KD', '5S', 'JC'], ['8H', '2S', '6D', 'TC'], ['QS', '3H', '7C'], ['4D', 'JH', '9S', '8H'], ['TD', '6C', '2H'], ['5H', 'KC', '3D', 'QH'], ['7S', 'AD', '4C'], ['JS', '9C', 'KH', '6H']];
            fcols.forEach(function (list, i) { sc.column(i, fy, 0, list); });
            sc.num(1, sc.X(1) - 15, -7); sc.num(2, sc.X(5) - 15, -7); sc.num(3, sc.X(1) - 15, fy - 7);
            body.appendChild(sc.el);
            legend([t('rf_l_cells'), t('rf_l_found'), t('rf_l_tab')]);
        }

        // ── 3. moves, each a scene ──────────────────────────────────────────
        h(t('rh_moves'));
        var figs = [];
        if (game === 'klondike' || game === 'freecell') {
            figs.push(figure(onTop('8S', '7H', true), t('r_alt_ok')));
            figs.push(figure(onTop('8D', '7H', false), t('r_alt_no')));
        }
        if (game === 'klondike') {
            // a run moves together: 9♣ 8♥ 7♣ onto the red ten
            sc = scene(exCw, 2.6, 1.95);
            sc.card(C('TD'), 0, 0);
            ['9C', '8H', '7C'].forEach(function (n, i) { sc.card(C(n), 1.6, sc.ch * 0.3 * i); });
            sc.arrow(sc.X(1.6) + exCw * 0.2, sc.ch * 0.15, sc.X(0) + exCw * 0.75, sc.ch * 0.38, 16);
            sc.mark(true);
            figs.push(figure(sc, t('rk_run')));
            // only a king to an empty column
            sc = scene(exCw, 2.6, 1.1);
            sc.slot(0, 0); sc.card(C('KS'), 1.6, 0);
            sc.arrow(sc.X(1.6), sc.ch * 0.55, sc.X(0) + exCw * 0.8, sc.ch * 0.5, 14);
            sc.mark(true);
            figs.push(figure(sc, t('rk_king')));
            sc = scene(exCw, 2.6, 1.1);
            sc.slot(0, 0); sc.card(C('QH'), 1.6, 0);
            sc.mark(false);
            figs.push(figure(sc, t('rk_king_no')));
            // home: by suit, up from the ace
            sc = scene(exCw, 2.6, 1.1);
            sc.card(C('AH'), 0, 0); sc.card(C('2H'), 0, 0);
            sc.card(C('3H'), 1.6, 0);
            sc.arrow(sc.X(1.6), sc.ch * 0.4, sc.X(0) + exCw * 0.8, sc.ch * 0.35, 14);
            sc.mark(true);
            figs.push(figure(sc, t('r_found')));
            // a card left on top of a face-down card turns over
            sc = scene(exCw, 1, 1.24);
            sc.back(0, 0); sc.back(0, sc.ch * 0.12); sc.card(C('5D'), 0, sc.ch * 0.24);
            figs.push(figure(sc, t('rk_flip')));
            grid(figs);

            h(t('rh_stock'));
            figs = [];
            sc = scene(exCw, 3.4, 1.1);
            sc.back(0, 0);
            var dn = v.draw === 3 ? ['6S', 'QD', '2C'] : ['QD'];
            dn.forEach(function (n, i) { sc.card(C(n), 1.4, 0).style.setProperty('--tx', (sc.X(1.4) + i * exCw * 0.3) + 'px'); });
            sc.arrow(sc.X(0) + exCw * 0.85, sc.ch * 0.3, sc.X(1.4) + exCw * 0.25, sc.ch * 0.3, 16);
            figs.push(figure(sc, t(v.draw === 3 ? 'rk_draw3' : 'rk_draw1')));
            sc = scene(exCw, 3.4, 1.1);
            var empty = sc.slot(0, 0);
            var again = icon('sol-i-again'); again.setAttribute('class', 'sol-slot-mark'); again.style.display = 'block';
            again.style.fill = 'none'; again.style.stroke = 'var(--slot-ink)'; again.style.strokeWidth = '1.8';
            empty.appendChild(again);
            sc.card(C('4S'), 1.4, 0); sc.card(C('JH'), 1.4, 0);
            sc.arrow(sc.X(1.4) + exCw * 0.2, sc.ch * 0.7, sc.X(0) + exCw * 0.8, sc.ch * 0.7, -14);
            figs.push(figure(sc, t('rk_recycle') + ' ' + t('rk_pass_' + (v.passes || 0))));
            grid(figs);
        } else if (game === 'spider') {
            sc = scene(exCw, 1, 1.32);
            sc.card(C(sp('8S')), 0, 0); sc.card(C(sp('7H')), 0, sc.ch * 0.32);
            sc.mark(true);
            figs.push(figure(sc, t('rs_any')));
            // a run of one suit moves as one
            sc = scene(exCw, 2.6, 1.95);
            sc.card(C(sp('TH')), 0, 0);
            ['9S', '8S', '7S'].forEach(function (n, i) { sc.card(C(sp(n)), 1.6, sc.ch * 0.3 * i); });
            sc.arrow(sc.X(1.6) + exCw * 0.2, sc.ch * 0.15, sc.X(0) + exCw * 0.75, sc.ch * 0.38, 16);
            sc.mark(true);
            figs.push(figure(sc, t('rs_run')));
            // a run broken by another suit (or a gap, in one suit): only its tail moves
            sc = scene(exCw, 1, 1.95);
            var broken = v.suits === 1 ? ['9S', '6S', '5S'] : ['9S', '8H', '7H'];
            broken.forEach(function (n, i) { sc.card(C(n), 0, sc.ch * 0.3 * i); });
            sc.mark(false);
            figs.push(figure(sc, t(v.suits === 1 ? 'rs_run_gap' : 'rs_run_no')));
            sc = scene(exCw, 2.6, 1.1);
            sc.slot(0, 0); sc.card(C(sp('5H')), 1.6, 0);
            sc.arrow(sc.X(1.6), sc.ch * 0.55, sc.X(0) + exCw * 0.8, sc.ch * 0.5, 14);
            sc.mark(true);
            figs.push(figure(sc, t('rs_empty')));
            // the whole suit, king to ace, leaves on its own
            var runCw = narrow ? 30 : 32;
            sc = scene(runCw, 3.2, 1 + 0.17 * 12);
            'KQJT98765432A'.split('').forEach(function (r, i) { sc.card(C(sp(r + 'S')), 0, sc.ch * 0.17 * i); });
            sc.card(C(sp('KS')), 2.2, 0);
            sc.arrow(sc.X(0) + runCw * 0.9, sc.ch * 1.6, sc.X(2.2) + runCw * 0.4, sc.ch * 1.05, 10);
            figs.push(figure(sc, t('rs_done'), true));
            grid(figs);

            h(t('rh_deal'));
            figs = [];
            sc = scene(30, 10, 1 + 0.3 + 1 + 0.3);
            for (var q = 0; q < 3; q++) sc.back(0, 0).style.setProperty('--tx', (q * 30 * 0.12) + 'px');
            var dealt = ['4H', 'JS', '9S', 'AH', '6S', 'QH', '2S', '8H', 'TS', '5S'];
            for (q = 0; q < 10; q++) {
                sc.back(q, sc.ch * 1.3);
                sc.card(C(sp(dealt[q])), q, sc.ch * 1.3 + sc.ch * 0.3);
            }
            sc.arrow(sc.X(0) + 24, sc.ch * 0.8, sc.X(4) + 15, sc.ch * 1.45, 6);
            figs.push(figure(sc, t('rs_deal'), true));
            sc = scene(exCw, 3.4, 1.1);
            sc.back(0, 0); sc.card(C(sp('6H')), 1.3, 0); sc.slot(2.4, 0);
            sc.mark(false);
            figs.push(figure(sc, t('rs_deal_no'), true));
            grid(figs);
        } else {
            // a free cell holds one card
            sc = scene(exCw, 2.6, 1.32);
            sc.slot(0, 0);
            sc.card(C('JC'), 1.6, 0); sc.card(C('4D'), 1.6, sc.ch * 0.32);
            sc.arrow(sc.X(1.6), sc.ch * 0.75, sc.X(0) + exCw * 0.8, sc.ch * 0.5, 14);
            sc.mark(true);
            figs.push(figure(sc, t('rf_cell')));
            sc = scene(exCw, 2.6, 1.1);
            sc.card(C('9D'), 0, 0); sc.card(C('6S'), 1.6, 0);
            sc.mark(false);
            figs.push(figure(sc, t('rf_cell_no')));
            sc = scene(exCw, 2.6, 1.1);
            sc.slot(0, 0); sc.card(C('5H'), 1.6, 0);
            sc.arrow(sc.X(1.6), sc.ch * 0.55, sc.X(0) + exCw * 0.8, sc.ch * 0.5, 14);
            sc.mark(true);
            figs.push(figure(sc, t('rf_empty')));
            sc = scene(exCw, 2.6, 1.1);
            sc.card(C('AS'), 0, 0); sc.card(C('2S'), 1.6, 0);
            sc.arrow(sc.X(1.6), sc.ch * 0.4, sc.X(0) + exCw * 0.8, sc.ch * 0.35, 14);
            sc.mark(true);
            figs.push(figure(sc, t('r_found')));
            // how many cards one move carries: (free cells + 1) × 2 for each empty column
            var lcw = narrow ? 30 : 32;
            sc = scene(lcw, 7.2, 1 + 0.12 + 1 + 0.26 * 5);
            sc.card(C('KD'), 0, 0); sc.card(C('QC'), 1, 0); sc.slot(2, 0); sc.slot(3, 0);
            var ly = sc.ch * 1.12;
            sc.slot(0, ly);
            ['9H', '8S', '7H', '6S', '5H', '4S'].forEach(function (n, i) { sc.card(C(n), 2.2, ly + sc.ch * 0.26 * i); });
            sc.card(C('TC'), 5.2, ly);
            sc.arrow(sc.X(2.2) + lcw * 0.9, ly + sc.ch * 0.3, sc.X(5.2) + lcw * 0.2, ly + sc.ch * 0.45, 22);
            sc.num(2, sc.X(2) + lcw * 0.6, -4);
            sc.num(1, lcw * 0.6, ly - 4);
            figs.push(figure(sc, t('rf_limit') + ' ' + t('rf_limit_ex'), true));
            grid(figs);
            p(t('rf_limit_why'));
            // the same arithmetic on the table as it stands now
            if (ctx.state && ctx.def && ctx.def.capacity) {
                var P = ctx.state.piles, cells = 0, cols0 = 0, j;
                for (j = 0; j < 4; j++) if (!P[j].length) cells++;
                for (j = 8; j < 16; j++) if (!P[j].length) cols0++;
                var into = (cells + 1) * Math.pow(2, cols0), intoEmpty = cols0 ? (cells + 1) * Math.pow(2, cols0 - 1) : 0;
                p(fill(t(cols0 ? 'rf_now' : 'rf_now0'), { cells: cells, cols: cols0, n: into, m: intoEmpty }), 'sol-r-now');
            }
        }

        // ── 4. scoring ──────────────────────────────────────────────────────
        h(t('rh_score'));
        if (game === 'klondike') {
            if (v.score === 'vegas') p(t('rk_score_vegas'));
            else { p(t('rk_score_std')); p(t(v.draw === 3 ? 'rk_score_pass3' : 'rk_score_pass1')); }
        } else if (game === 'spider') p(t('rs_score'));
        else { p(t('rf_score')); p(t('rf_numbers')); }

        // ── 5. how to play on this table ────────────────────────────────────
        h(t('rh_play'));
        var ul = el('ul', 'sol-rk');
        ['rp_tap', 'rp_drag', 'rp_double', game === 'spider' ? '' : 'rp_right', 'rp_why'].forEach(function (k) { if (k) ul.appendChild(el('li', null, t(k))); });
        var keys = el('li');
        keyLine(keys, t('rp_keys'));
        ul.appendChild(keys);
        body.appendChild(ul);

        // ── 6. the verdict and the deal number ──────────────────────────────
        h(t('rh_verdict'));
        var rv = el('div', 'sol-rv');
        [['is-ok', 'v_ok', 'rv_ok'], ['is-dead', 'v_dead', 'rv_dead'], ['', 'v_idk', 'rv_idk']].forEach(function (row) {
            var felt = el('div', 'sol-rv-felt'), badge = el('span', 'sol-v ' + row[0]);
            badge.appendChild(el('i'));
            badge.appendChild(el('span', null, t(row[1])));
            felt.appendChild(badge);
            rv.appendChild(felt);
            rv.appendChild(el('p', null, t(row[2])));
        });
        var felt2 = el('div', 'sol-rv-felt'), deal = el('span', 'sol-deal', fill(t('deal_short'), { n: ctx.number || 617 }));
        felt2.appendChild(deal);
        rv.appendChild(felt2);
        rv.appendChild(el('p', null, t('rv_number')));
        body.appendChild(rv);

        // ── 7. hints, undo and records ──────────────────────────────────────
        h(t('rh_help'));
        p(t('rv_help'));
        if (game === 'spider') p(t('rv_help_spider'));
    }

    // "D — колода · Z / Y — …": the letters before a dash are keys.
    function keyLine(li, text) {
        var parts = String(text).split(/(\s·\s|,\s)/);
        parts.forEach(function (part) {
            var m = /^(.*?)(\s[—–-]\s)(.*)$/.exec(part);
            if (!m) { li.appendChild(document.createTextNode(part)); return; }
            m[1].split(/(\s\/\s|\s)/).forEach(function (k) {
                if (/^\s*\/?\s*$/.test(k) || k.length > 12) li.appendChild(document.createTextNode(k));
                else { var kb = document.createElement('kbd'); kb.textContent = k; li.appendChild(kb); }
            });
            li.appendChild(document.createTextNode(m[2] + m[3]));
        });
    }

    root.SolRules = { build: build };
})(typeof self !== 'undefined' ? self : this);
